import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, Mail, Phone, MapPin, Award, AlertCircle } from "lucide-react";
import Logo from "./Logo";
import SocialLinks from "./SocialLinks";
import TrustpilotWidget from "./TrustpilotWidget";
import scheduleIdleOrInteraction from "../../lib/scheduleIdleOrInteraction";
import { useConciergeNumber, formatWaDisplay } from "../../lib/concierge";

const TREE_NATION_SRC = "https://widgets.tree-nation.com/js/widgets/v3/widgets.min.js";

export default function Footer() {
    const { t } = useTranslation();
    const footerWa = useConciergeNumber();
    useEffect(() => {
        // Lazy-inject the Tree-Nation + Trustpilot widget loaders. Both scripts
        // are heavy (Trustpilot 110 KiB bundle; Tree-Nation pulls a 400 KiB
        // nested Nuxt SPA with its own Google Fonts chain — the famous
        // "Outfit font in critical path" PSI warning). They're below-the-fold
        // on every public page, so we defer the script tags until the browser
        // is idle OR the user interacts — whichever comes first. The widget
        // <div>s render as styled placeholders in the meantime; once the
        // loaders mount they auto-scan and hydrate. Net effect: LCP recovers
        // ~1.5 s without changing what guests see.
        if (typeof window === "undefined") return undefined;
        const cleanup = scheduleIdleOrInteraction(() => {
            if (!document.querySelector(`script[src="${TREE_NATION_SRC}"]`)) {
                const s = document.createElement("script");
                s.src = TREE_NATION_SRC;
                s.async = true;
                document.body.appendChild(s);
            }
        });
        return cleanup;
    }, []);
    return (
        <footer className="bg-primary text-primary-foreground mt-24" data-testid="site-footer">
            <div className="container-page py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3 mb-6">
                        <Logo size={44} />
                        <span className="font-serif text-2xl">MyEcoTour</span>
                    </div>
                    <p className="font-serif italic text-lg text-primary-foreground/90 mb-2">Nature and Adventure</p>
                    <p className="text-sm leading-relaxed text-primary-foreground/75 max-w-md">
                        A bespoke travel concierge crafting eco-conscious journeys of quiet luxury across India, Asia, Africa, Europe and the Americas. Quietly considered. Carefully sourced.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-xs text-primary-foreground/65 uppercase tracking-[0.2em]" data-testid="footer-pillars">
                        <span className="inline-flex items-center gap-2"><Leaf size={12} strokeWidth={1.5} /> Carbon-Neutral Journeys</span>
                        <span>·</span>
                        <span>Net Zero Operations</span>
                        <span>·</span>
                        <span>Net Zero Website</span>
                    </div>
                    <div className="mt-8 inline-flex items-start gap-3 px-4 py-3 border border-accent/40 max-w-sm" data-testid="footer-award">
                        <Award size={20} strokeWidth={1.5} className="text-accent mt-0.5 shrink-0" />
                        <div className="text-xs leading-snug">
                            <div className="text-primary-foreground font-semibold">India 5000 Best MSME Awards</div>
                            <div className="text-primary-foreground/65 mt-1">Winner · 2024</div>
                        </div>
                    </div>

                    <div className="mt-6" data-testid="footer-trustpilot">
                        <p className="overline text-primary-foreground/55 mb-3">Reviewed on</p>
                        <TrustpilotWidget starSize={24} />
                    </div>

                    <div className="mt-10">
                        <h3 className="overline text-primary-foreground/60 mb-4">Follow the journey</h3>
                        <SocialLinks tone="light" testIdPrefix="footer-social" />
                    </div>
                </div>

                <div className="md:col-span-3">
                    <h3 className="overline text-primary-foreground/60 mb-5">{t("footer.explore")}</h3>
                    <ul className="space-y-3 text-sm">
                        <li><Link to="/destinations" className="hover:text-accent transition-colors">{t("nav.destinations")}</Link></li>
                        <li><Link to="/experiences" className="hover:text-accent transition-colors">{t("nav.experiences")}</Link></li>
                        <li><Link to="/retreats" className="hover:text-accent transition-colors">{t("nav.retreats")}</Link></li>
                        <li><Link to="/corporate" className="hover:text-accent transition-colors" data-testid="footer-corporate">{t("nav.corporate")}</Link></li>
                        <li><Link to="/journal" className="hover:text-accent transition-colors">{t("nav.journal")}</Link></li>
                        <li><Link to="/about" className="hover:text-accent transition-colors">{t("nav.about")}</Link></li>
                        <li><Link to="/impact" className="hover:text-accent transition-colors">{t("nav.impact")}</Link></li>
                        <li><Link to="/sustainability" className="hover:text-accent transition-colors" data-testid="footer-sustainability">Sustainability Methodology</Link></li>
                        <li><Link to="/loyalty" className="hover:text-accent transition-colors" data-testid="footer-loyalty">{t("nav.rewards")}</Link></li>
                        <li><Link to="/contact" className="hover:text-accent transition-colors">{t("nav.enquire")}</Link></li>
                    </ul>

                    <div className="mt-8" data-testid="footer-tree-counter">
                        <h3 className="overline text-primary-foreground/60 mb-4">Our Forest</h3>
                        <div className="tree-nation-widget bg-primary-foreground/5 border border-primary-foreground/15 p-4 rounded-sm">
                            <div
                                data-widget-type="tree-counter"
                                data-tree-nation-code="275f463178ff5fda"
                                data-lang="en"
                                data-theme="light"
                            ></div>
                        </div>
                        <div className="tree-nation-widget bg-primary-foreground/5 border border-primary-foreground/15 p-4 rounded-sm mt-3" data-testid="footer-tree-offset">
                            <div
                                data-widget-type="offset-website"
                                data-tree-nation-code="642456bc175ac"
                                data-lang="en"
                                data-theme="light"
                            ></div>
                        </div>
                    </div>

                    <h3 className="overline text-primary-foreground/60 mb-5 mt-10">{t("footer.legal")}</h3>
                    <ul className="space-y-3 text-sm">
                        <li><Link to="/legal/cancellation-refund" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-legal-cancellation">{t("footer.cancellation")}</Link></li>
                        <li><Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-legal-terms">Website Terms of Use</Link></li>
                        <li><Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-legal-privacy">{t("footer.privacy")}</Link></li>
                        <li><Link to="/legal/customer-agreement" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-legal-cmua">Customer Master User Agreement</Link></li>
                        <li><Link to="/legal/partner-terms" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-legal-partner">Partner Terms</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h3 className="overline text-primary-foreground/60 mb-5">{t("footer.contact")}</h3>
                    <div className="space-y-4 text-sm text-primary-foreground/85">
                        <div className="flex items-start gap-3">
                            <Phone size={14} strokeWidth={1.5} className="mt-1 text-accent shrink-0" />
                            <div data-testid="footer-phones">
                                <div>022-47510261</div>
                                <div className="mt-0.5" data-testid="footer-concierge-number">{formatWaDisplay(footerWa)}</div>
                                <div className="text-primary-foreground/55 text-xs mt-2 leading-snug">
                                    Mon – Sat · 9:30 am – 6:30 pm IST<br />
                                    (GMT +05:30 Hrs)
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Mail size={14} strokeWidth={1.5} className="mt-1 text-accent shrink-0" />
                            <a href="mailto:info@myecotour.com" className="hover:text-accent transition-colors" data-testid="footer-email">info@myecotour.com</a>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={14} strokeWidth={1.5} className="mt-1 text-accent shrink-0" />
                            <div className="text-primary-foreground/85" data-testid="footer-address">
                                <div className="font-semibold text-primary-foreground">Whispering Earth LLP</div>
                                <div className="mt-1">
                                    G-116, B Wing, Express Zone,<br />
                                    Western Express Highway,<br />
                                    Malad East, Mumbai – 400097
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-t border-primary-foreground/10">
                <div className="container-page py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-primary-foreground/55">
                    <div className="leading-relaxed" data-testid="footer-copyright">
                        Copyright © {new Date().getFullYear()} <span className="text-primary-foreground/75">Whispering Earth LLP</span>. All rights reserved. Powered by <span className="text-primary-foreground/75">MyEcoTour</span>. <span className="block sm:inline">Whispering Earth LLP is the holding company of the MyEcoTour brand.</span>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <Link to="/partner/login" className="hover:text-accent transition-colors" data-testid="footer-partner-link">Partner Portal</Link>
                        <Link to="/admin/login" className="hover:text-accent transition-colors" data-testid="footer-admin-link">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
